import { useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * SAB-1004 — one place that decides how a team is pictured.
 *
 * Every team surface previously inlined `name.charAt(0)` in its own circle, so adding
 * logos would have meant six near-identical edits and six chances to disagree about the
 * fallback. This is the single answer: a logo when the team has one, the monogram when it
 * does not.
 *
 * ── logo_url, NOT avatar_url ────────────────────────────────────────────────
 *
 * `nfl_teams` has a `logo_url` column and NO `avatar_url` column -- verified on staging
 * and prod. Both NFLLeagueContext's NFLTeam type and NFLMatchupsPage nevertheless referred
 * to `avatar_url`, so those reads were always `undefined`: a field that could never carry
 * an image, on the surfaces meant to show one. Corrected alongside this component.
 *
 * ── FALLBACK IS A REAL STATE, NOT AN ERROR ──────────────────────────────────
 *
 * Zero teams have a logo today, so the monogram is what almost everyone sees. It also has
 * to survive a URL that 404s -- a logo reset by a commissioner leaves league-mates holding
 * a stale URL until their next refetch -- so a broken image falls back rather than
 * rendering the browser's torn-page icon.
 */
export interface TeamLogoProps {
  logoUrl?: string | null;
  name?: string | null;
  /** Tailwind sizing for the container; callers own their own dimensions. */
  className?: string;
  /** Monogram text size, since callers range from an 11px chip to a 44px header. */
  textClassName?: string;
  rounded?: 'full' | 'xl';
}

/** First letter, uppercased. '?' when there is no name to take one from. */
export function teamMonogram(name?: string | null): string {
  const ch = (name ?? '').trim().charAt(0);
  return ch ? ch.toUpperCase() : '?';
}

export default function TeamLogo({
  logoUrl, name, className, textClassName, rounded = 'full',
}: TeamLogoProps) {
  // A URL that fails to load must degrade to the monogram, not to a broken-image glyph.
  const [failed, setFailed] = useState(false);
  const showImage = !!logoUrl && !failed;
  const shape = rounded === 'full' ? 'rounded-full' : 'rounded-xl';

  return (
    <div
      data-testid="team-logo"
      data-has-logo={showImage ? 'true' : 'false'}
      className={cn(
        'flex items-center justify-center shrink-0 overflow-hidden',
        shape,
        showImage ? 'bg-muted' : 'bg-primary/10 border border-primary/20',
        className,
      )}
    >
      {showImage ? (
        <img
          src={logoUrl!}
          alt={name ? `${name} logo` : 'Team logo'}
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className={cn('font-bold text-primary', textClassName)}>{teamMonogram(name)}</span>
      )}
    </div>
  );
}
